import React, { useState } from 'react';
import { BodyText, DropdownValue, FlexColumn, FlexRow, SvgIcon } from '@make-software/cspr-design';
import {
  DropdownStyles,
  ErrorText,
  ErrorTextContainer,
  FormContainer,
  StyledConfirmButton,
  StyledDismissButton,
  StyledInput
} from 'app/components/common/common-styles';
import Select, { components } from 'react-select';
import { createApp, PurposeType, AppType } from 'app/console-api/apps/apps';
import styled from 'styled-components';
import { appsCreateStep1ValidationSchema } from 'app/components/management/apps/common/apps-validation-schema';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, CustomEvents, statusCode } from 'app/constants';
import { APPS_EDIT_INTEGRATION_PATH, SIGN_IN_PATH } from 'app/route/paths';
import { ErrorResult } from 'app/organizations-api/types';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import ErrorEntityWindow from 'app/components/common/error-entity-modal/error-entity-modal';
import { AppsCategoriesSelect } from 'app/components/management/apps/common/apps-categories-select';
import { WizardProgress } from 'app/components/management/apps/create/wizard-progress';
import { AppContactsInput } from 'app/components/management/apps/common/app-contacts-input';
import { Contact } from 'app/console-api/apps/apps';

export const StyledCaption = styled.div<{ margin?: string }>(({ theme, margin }) =>
  theme.withMedia({
    margin: margin ? margin : '20px 0 20px 0'
  })
);

const StyledButtonRow = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    marginBottom: '40px'
  })
);

const OptionDescription = styled.div(({ theme }) =>
  theme.withMedia({
    color: theme.styleguideColors.contentSecondary,
    fontSize: '11px',
    fontWeight: 400,
    lineHeight: '16px'
  })
);

const { Option, SingleValue } = components;

const DropdownOption = (props: any) => {
  const {
    data: { label, description }
  } = props;
  return (
    <Option {...props}>
      <div>
        <div>{label}</div>
        <OptionDescription>{description}</OptionDescription>
      </div>
    </Option>
  );
};

const SelectedDropdownValue = (props: any) => (
  <SingleValue {...props}>{props.data.label}</SingleValue>
);

const PurposeDropdownValues = [
  {
    value: PurposeType.Production.toString(),
    label: 'Production (recommended)',
    description:
      'Use this option when working with production environments, data at scale or professional use cases'
  },
  {
    value: PurposeType.Development.toString(),
    label: 'Development',
    description: 'Use this option when testing out new ideas or starter projects.'
  }
];

const DefaultPurpose = {
  value: PurposeType.Production.toString(),
  label: 'Production (recommended)'
};

const TsTypeDropdownValues = [
  {
    value: AppType.TelegramMiniApp.toString(),
    label: 'Telegram Mini app',
    description:
      'Native Telegram Mini App that runs inside Telegram and can be opened from any chat.'
  },
  {
    value: AppType.TelegramBot.toString(),
    label: 'Telegram bot',
    description: 'Web App connected to the Telegram bot.'
  }
];

const DefaultTsType = {
  value: AppType.TelegramMiniApp.toString(),
  label: 'Telegram Mini app'
};

const AppsCreateStep1: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    type: DefaultTsType,
    claimNotificationUrl: '',
    categories: [] as DropdownValue[],
    contacts: [] as Contact[],
    purpose: DefaultPurpose,
    mau: 0,
    dau: 0
  });
  const [openConflictErrorModal, setOpenConflictErrorModal] = useState<boolean>(false);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const [conflictError, setConflictError] = useState<string>('');
  const navigate = useNavigate();

  const {
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(appsCreateStep1ValidationSchema),
    defaultValues: formData
  });

  const handleInputChange = <K extends keyof typeof formData>(
    key: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
    // @ts-ignore
    setValue(key, value);
    // @ts-ignore
    clearErrors(key);
  };

  const handleSubmitForm = async () => {
    createApp({
      ...formData,
      ...{
        purpose: formData.purpose.value as PurposeType,
        type: formData.type.value as AppType,
        categories: formData.categories.map((category) => category.value),
        contacts: formData.contacts
      }
    })
      .then((res) => {
        window.dispatchEvent(new CustomEvent(CustomEvents.appCreated));
        navigate({
          pathname: generatePath(APPS_EDIT_INTEGRATION_PATH, { appID: res.id }),
          search: createSearchParams({ referer: 'onboarding' }).toString()
        });
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.conflict_error:
              setConflictError(error.info?.error.description || error.message);
              setOpenConflictErrorModal(true);
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to create app', error);
      });
  };

  const handleOnDismiss = () => {
    clearErrors();
    navigate(-1);
  };

  const handleCloseConflictErrorModal = () => {
    setOpenConflictErrorModal(false);
  };

  const sentData = () => {
    handleSubmit(handleSubmitForm)();
  };

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <FormContainer>
      <ErrorEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseConflictErrorModal}
        isOpen={openConflictErrorModal}
        icon={<SvgIcon src={NotFoundIcon} width={195} height={120} />}
        title={conflictError}
        information={''}
      />
      <FlexColumn gap={40}>
        <WizardProgress step={1}></WizardProgress>
        <FlexColumn gap={10}>
          <StyledCaption>
            <BodyText size={1} scale={'lg'}>
              Application details
            </BodyText>
          </StyledCaption>
          <FlexColumn gap={10}>
            <FlexColumn gap={4}>
              <BodyText size={1}>Type</BodyText>
              <Select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e as unknown as DropdownValue)}
                options={TsTypeDropdownValues}
                components={{ Option: DropdownOption, SingleValue: SelectedDropdownValue }}
                styles={DropdownStyles()}
              />
            </FlexColumn>
            <StyledInput
              name={'name'}
              label={<BodyText size={1}>Name</BodyText>}
              placeholder={'App name'}
              value={formData.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleInputChange('name', e.target.value);
              }}
            />
            {errors?.name?.message && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors?.name?.message}</ErrorText>
              </ErrorTextContainer>
            )}
            <StyledInput
              label={<BodyText size={1}>Description</BodyText>}
              placeholder={'Describe your app here'}
              name={'description'}
              value={formData.description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange('description', e.target.value)
              }
            />
            {errors?.description?.message && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors?.description?.message}</ErrorText>
              </ErrorTextContainer>
            )}
            <FlexColumn gap={4}>
              <BodyText size={1}>Purpose</BodyText>
              <Select
                value={formData.purpose}
                onChange={(e) => handleInputChange('purpose', e as unknown as DropdownValue)}
                options={PurposeDropdownValues}
                components={{ Option: DropdownOption, SingleValue: SelectedDropdownValue }}
                styles={DropdownStyles()}
              />
            </FlexColumn>
            <FlexColumn>
              <StyledCaption margin={'0 0 4px 0'}>
                <BodyText size={1} scale={'sm'}>
                  Categories
                </BodyText>
              </StyledCaption>
              <AppsCategoriesSelect
                categories={formData.categories}
                handleInputChange={(e: any) =>
                  handleInputChange('categories', e as unknown as DropdownValue[])
                }
              />
            </FlexColumn>
            {errors?.categories?.message && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors?.categories?.message}</ErrorText>
              </ErrorTextContainer>
            )}
            <AppContactsInput
              contacts={formData.contacts}
              onChange={(contacts) => handleInputChange('contacts', contacts)}
            />
            <StyledInput
              name={'dau'}
              label={<BodyText size={1}>Daily Active Users (DAU)</BodyText>}
              value={formData.dau}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange('dau', Number(e.target.value))
              }
            />
            {errors?.dau?.message && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors?.dau?.message}</ErrorText>
              </ErrorTextContainer>
            )}
            <StyledInput
              name={'mau'}
              label={<BodyText size={1}>Monthly Active Users (MAU)</BodyText>}
              value={formData.mau}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChange('mau', Number(e.target.value))
              }
            />
            {errors?.mau?.message && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors?.mau?.message}</ErrorText>
              </ErrorTextContainer>
            )}
          </FlexColumn>
        </FlexColumn>
        <StyledButtonRow justify="space-between" gap={10}>
          <StyledDismissButton color={'secondaryBlue'} onClick={handleOnDismiss}>
            Cancel
          </StyledDismissButton>
          <StyledConfirmButton color="primaryBlue" onClick={sentData}>
            Next
          </StyledConfirmButton>
        </StyledButtonRow>
      </FlexColumn>
    </FormContainer>
  );
};

export default AppsCreateStep1;
